<template>
	<div class='faq-container p-4 max-w-4xl mx-auto'>
		<h1 class='text-2xl font-bold mb-6'>Dashdocs FAQs</h1>

		<div v-for='(category, index) in faqData' :key='index' class='mb-8'>
			<h2 class='text-xl font-semibold mb-4'>{{ category.section }}</h2>

			<div v-for='(qa, qIndex) in category.qas' :key='qIndex' class='mb-4 border rounded overflow-hidden'>
				<button @click='toggle(category.section, qIndex)'
					class='w-full text-left px-4 py-2 bg-primary-800 text-white hover:bg-yellow-600 font-medium'>
					{{ qa.question }}
				</button>
				<div v-if='isOpen(category.section, qIndex)' class='px-4 py-3 bg-gray-100'>
					<p v-html='qa.answer'></p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'FaqSection',
	data() {
		return {
			openQuestions: {},
			faqData: [
				{
					section: 'Product Overview',
					qas: [
						{
							question: 'What specific data capture challenges does Dashdocs address?',
							answer: `Dashdocs helps organizations overcome challenges related to manual data entry errors, slow processing times, 
							inconsistent formatting in documents, and extracting information from unstructured data sources such as PDFs, scanned documents, 
							and handwritten forms.<br><br>It enables organisations to reallocate people from less mundane, manual tasks. It also provides constant availability and responsiveness.`,
						},
						{
							question: 'How does Dashdocs handle data extraction from PDFs, handwritten forms, and other formats?',
							answer: `Dashdocs employs Cloud Based Optical Character Recognition (OCR) and Intelligent Character Recognition (ICR) for PDFs 
							and handwritten documents. Our proprietary custom algorithms enhance accuracy by recognizing context and structured patterns in various formats.`,
						},
						{
							question: 'What kinds of organizations or departments benefit most from automated data capturing?',
							answer: `Industries such as finance, healthcare, legal, logistics, and government, as well as department’s handling high volumes of 
							similar documents (HR, accounting, compliance, and customer service), benefit greatly from automated data capture.`,
						},
						{
							question: 'How does Dashdocs compare to other data capture solutions in terms of accuracy and efficiency?',
							answer: 'Dashdocs offers similar capabilities to more expensive competitor products at a fraction of the cost.',
						},
					]
				},
				{
					section: 'Features and Capabilities',
					qas: [
						{
							question: 'How does Dashdocs ensure accuracy when extracting data from handwritten forms?',
							answer: `Dashdocs leverages ICR technology alongside AI-driven custom algorithms to interpret even difficult handwriting. 
							We also use human review processes to validate low confidence results. We employ continuous machine learning to refine accuracy over time.`,
						},
						{
							question: 'Are there limitations to the types of documents Dashdocs can process?',
							answer: 'DashDocs can process any readable document of recognised layout. Once configured,Dashdocs should provide excellent result in most cases. While highly versatile, Dashdocs may struggle with severely degraded documents, extremely illegible handwriting, and certain complex graphical elements that do not conform to text-based recognition models.',
						},
						{
							question: 'How does Dashdocs handle discrepancies or incomplete data in the extraction process?',
							answer: 'Dashdocs flags incomplete or ambiguous data and applies confidence scoring, allowing human review where necessary.',
						},
						{
							question: 'Can Dashdocs work offline or in low-connectivity environments, especially for remote data capture?',
							answer: 'Dashdocs is a real time service and thus requires connnectivity to function.<br>It is on our development roadmap tp offer an offline mode with local processing capabilities, ensuring that data can be captured and synchronized once connectivity is restored.',
						},
						{
							question: 'How does Dashdocs scale its performance for small datasets versus large volumes?',
							answer: `Dashdocs utilizes cloud-based processing for large datasets, can be optimized batch processing to ensure 
							scalability without performance degradation.`,
						},
						{
							question: 'Are there specific tools or algorithms that make Dashdocs excel in data population tasks?',
							answer: 'Yes, Dashdocs integrates AI-driven template recognition to populate fields with high accuracy.',
						},
					]
				},
				{
					section: 'Client Benefits',
					qas: [
						{
							question: 'How much time does Dashdocs save?',
							answer:
								'With existing clients Dashdocs can perform at least 80% faster than traditional data capture methods on the same document, with almost all tasks completed in under 1 minute. In addition tasks can be processed in parallel resulting in exponential improvements in efficiency.',
						},
						{
							question: 'What are the benefits for enterprises?',
							answer:
								'Benefits include reduced costs, improved compliance, accuracy, and operational efficiency.',
						},
						{
							question: 'How does Dashdocs reduce manual errors?',
							answer:
								'Dashdocs provides confidence level flagging and trained models to avoid fatigue-related mistakes.',
						},
						{
							question: 'What productivity gains can teams expect?',
							answer:
								'Teams can expect faster processing of critical business data, lower stress, better decisions from accurate data.',
						},
						{
							question: 'How does it impact operational efficiency?',
							answer:
								'Dashdocs can eliminate processing bottlenecks and accelerate workflows.',
						},
					],
				},
				{
					section: 'Use Cases',
					qas: [
						{
							question: 'How do clients use Dashdocs?',
							answer:
								'Examples include insurance schedules, student intake, loan applicationss, patient forms, shipping invoices, and contracts.',
						},
						{
							question: 'What industries beyond sales and insurance can benefit?',
							answer:
								'Healthcare, education, legal, banking, logistics, government, retail.',
						},
						{
							question: 'What is a typical processing workflow?',
							answer:
								'Ingestion → Pre-processing → Extraction → Review → Integration/Export',
						},
						{
							question: 'How does Dashdocs support compliance?',
							answer:
								'Dashdocs provides automated audits, accurate records, secure data and inconsistency flags.',
						},
						{
							question: 'What unexpected use cases exist?',
							answer:
								'Checkbox/signature reading, ID documnents, large insurance schedules.',
						},
					],
				},
				{
					section: 'Implementation and Support',
					qas: [
						{
							question: 'What do clients need before implementing?',
							answer:
								'We require sample documents/data and identified fields for extraction. From here we can quick create of proof of concept customised for your use case',
						},
						{
							question: 'Does it work with legacy systems?',
							answer:
								'Yes. API and file-based exports ensure compatibility.',
						},
						{
							question: 'How is data secured?',
							answer:
								'Dashdocs employs best of breed encryption, access controls and secure cloud storage best practices.',
						},
						{
							question: 'How customizable is Dashdocs?',
							answer:
								'Highly customizable with tailored integrations.',
						},
						{
							question: 'What customer feedback has been received?',
							answer:
								'Our clients report that Dashdocs is intuitive, provides fast client onboarding, they have experienced responsive support and a smooth adoption of the technology into their business.',
						},
					],
				},
			],
		};
	},
	methods: {
		toggle(section, index) {
			const key = `${section}-${index}`;
			this.openQuestions[key] = !this.openQuestions[key];
		},
		isOpen(section, index) {
			return this.openQuestions[`${section}-${index}`];
		},
	},
};
</script>

<style scoped>
.faq-container h2 {
	border-bottom: 2px solid #ddd;
	padding-bottom: 0.25rem;
}
</style>
